import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Brain, Sparkles, Target, ArrowRight, ChevronRight, User,
  FileText, Trophy, BarChart3, Shield, Calculator, ClipboardList,
  Loader2, BookOpen, CheckCircle, AlertCircle, Award, Star,
  Clipboard, Circle, HelpCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import api, { kaireraAPI, gamificationAPI, dataAPI, linkedinAPI } from '../lib/api';
import { useSubscription } from '../lib/subscription';
import { toast } from 'sonner';
import OnboardingTutorial from '../components/OnboardingTutorial';

const ICON_MAP = {
  'user': User, 'check': CheckCircle, 'file': FileText, 'bar-chart': BarChart3,
  'award': Award, 'brain': Brain, 'sparkles': Sparkles, 'shield': Shield,
  'star': Star, 'clipboard': Clipboard, 'target': Target, 'trophy': Trophy,
};

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, delay, ease: [0.4, 0, 0.2, 1] },
});

export default function DashboardPage() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [ciData, setCiData] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [linkedinDone, setLinkedinDone] = useState(false);
  const { canAccess } = useSubscription();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [profileRes, ciRes, gamRes, sessRes, tlRes, liRes] = await Promise.allSettled([
          api.get('/onboarding/status'),
          kaireraAPI.getDashboard(),
          gamificationAPI.profile(),
          kaireraAPI.getSessions(),
          dataAPI.timeline(),
          linkedinAPI.profile(),
        ]);
        if (profileRes.status === 'fulfilled') setProfile(profileRes.value.data);
        if (ciRes.status === 'fulfilled') setCiData(ciRes.value.data);
        if (gamRes.status === 'fulfilled') setGamification(gamRes.value.data);
        if (sessRes.status === 'fulfilled') setSessions(sessRes.value.data?.sessions || []);
        if (tlRes.status === 'fulfilled') setTimeline(tlRes.value.data);
        if (liRes.status === 'fulfilled') setLinkedinDone(!!liRes.value.data?.completed);
      } catch (err) {
        console.error('Dashboard load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Show tutorial for first-time users
  useEffect(() => {
    if (!loading) {
      const tutorialDone = localStorage.getItem('kairera_tutorial_done');
      if (!tutorialDone) {
        const timer = setTimeout(() => setShowTutorial(true), 800);
        return () => clearTimeout(timer);
      }
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#0D9488' }} />
      </div>
    );
  }

  const hasResume = profile?.has_resume;
  const hasCI = ciData?.has_session;
  const discoveryComplete = ciData?.discovery_complete;
  const completionPct = ciData?.completion_pct || 0;
  const gradedCount = ciData?.grades?.filter(g => g.state !== 'locked')?.length || 0;

  const QUOTES = [
    { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
    { text: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
    { text: "Your career is a marathon, not a sprint. Every step counts.", author: "Kairera" },
    { text: "The best time to plant a tree was 20 years ago. The second best time is now.", author: "Chinese Proverb" },
    { text: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
    { text: "Opportunities don't happen. You create them.", author: "Chris Grosser" },
    { text: "The future depends on what you do today.", author: "Mahatma Gandhi" },
  ];
  const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  const dailyQuote = QUOTES[dayOfYear % QUOTES.length];

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Onboarding Tutorial Overlay */}
      {showTutorial && (
        <OnboardingTutorial onComplete={() => setShowTutorial(false)} />
      )}

      {/* Header */}
      <motion.div {...fadeIn(0)}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1" style={{ color: '#0D9488' }}>Dashboard</p>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ color: 'var(--t-text-heading)' }}>
              Welcome back{profile?.name ? `, ${profile.name.split(' ')[0]}` : ''}
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--t-text-muted)' }}>Your career intelligence at a glance</p>
          </div>
          <button
            onClick={() => { localStorage.removeItem('kairera_tutorial_done'); setShowTutorial(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-colors"
            style={{ color: 'var(--t-text-muted)', background: 'var(--t-bg-section)', border: '1px solid var(--t-border-subtle)' }}
            data-testid="restart-tutorial-btn"
            title="Restart tutorial">
            <HelpCircle className="w-3.5 h-3.5" /> Guide
          </button>
        </div>
      </motion.div>

      {/* Daily Motivation Quote */}
      <motion.div {...fadeIn(0.02)} className="nx-card p-4 flex items-center gap-4" data-testid="daily-quote"
        style={{ borderLeft: '3px solid #0D9488', background: 'rgba(13,148,136,0.03)' }}>
        <Sparkles className="w-5 h-5 flex-shrink-0" style={{ color: '#0D9488' }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium italic" style={{ color: 'var(--t-text-heading)' }}>"{dailyQuote.text}"</p>
          <p className="text-[10px] mt-0.5 font-bold" style={{ color: 'var(--t-text-muted)' }}>{dailyQuote.author}</p>
        </div>
      </motion.div>

      {/* Career Readiness Score */}
      {(() => {
        const totalDims = ciData?.grades?.length || 13;
        const gradedDimsCount = ciData?.grades?.filter(g => g.state !== 'locked')?.length || 0;
        const batteryPct = totalDims > 0 ? Math.round((gradedDimsCount / totalDims) * 100) : 0;

        const steps = [
          { key: 'resume', label: 'Upload Resume', done: !!hasResume, pct: hasResume ? 100 : 0, path: '/profile', weight: 15 },
          { key: 'skills', label: 'Add Skills', done: (profile?.skills_count || 0) > 0, pct: (profile?.skills_count || 0) > 0 ? 100 : 0, path: '/profile', weight: 10 },
          { key: 'discovery', label: 'Career Discovery', done: !!discoveryComplete, pct: completionPct, path: '/career-assessment', weight: 25 },
          { key: 'batteries', label: 'Assessment Batteries', done: batteryPct >= 80, pct: batteryPct, path: '/career-batteries', weight: 25 },
          { key: 'linkedin', label: 'LinkedIn Optimizer', done: linkedinDone, pct: linkedinDone ? 100 : 0, path: '/linkedin-optimizer', weight: 15 },
          { key: 'graded', label: 'Dimensions Graded', done: gradedDimsCount >= totalDims, pct: batteryPct, path: '/career-intel', weight: 10 },
        ];
        const totalWeight = steps.reduce((sum, s) => sum + s.weight, 0);
        const readinessScore = Math.round(steps.reduce((sum, s) => sum + (s.pct / 100) * s.weight, 0) / totalWeight * 100);
        const doneCount = steps.filter(s => s.done).length;

        // Color based on score
        const scoreColor = readinessScore >= 80 ? '#16A34A' : readinessScore >= 50 ? '#F5841F' : readinessScore >= 25 ? '#EAB308' : '#DC2626';

        return (
          <motion.div {...fadeIn(0.05)} className="nx-card p-5" data-testid="career-readiness-score">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center relative"
                  style={{ background: `${scoreColor}10`, border: `2px solid ${scoreColor}` }}>
                  <span className="text-xl font-black" style={{ color: scoreColor }}>{readinessScore}%</span>
                </div>
                <div>
                  <h3 className="text-base font-extrabold" style={{ color: 'var(--t-text-heading)' }}>
                    Career Readiness Score
                  </h3>
                  <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>
                    {doneCount}/{steps.length} milestones complete
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold" style={{ color: scoreColor }}>
                  {readinessScore >= 80 ? 'Market Ready' : readinessScore >= 50 ? 'Getting There' : readinessScore >= 25 ? 'Building Up' : 'Just Starting'}
                </p>
              </div>
            </div>

            {/* Full progress bar */}
            <div className="h-2.5 rounded-full overflow-hidden mb-4" style={{ background: 'var(--t-border)' }}>
              <div className="h-full rounded-full transition-all duration-700"
                style={{ width: `${readinessScore}%`, background: `linear-gradient(90deg, ${scoreColor}, ${scoreColor}cc)` }} />
            </div>

            {/* Steps */}
            <div className="space-y-2">
              {steps.map(step => (
                <button key={step.key} onClick={() => navigate(step.path)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all group"
                  style={{ background: step.done ? 'rgba(13,148,136,0.04)' : 'var(--t-bg-page)', border: '1px solid var(--t-border-subtle)' }}
                  data-testid={`readiness-step-${step.key}`}>
                  {step.done
                    ? <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#0D9488' }} />
                    : <Circle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--t-text-muted)' }} />}
                  <span className="text-xs font-semibold flex-1" style={{ color: step.done ? '#0D9488' : 'var(--t-text-heading)' }}>
                    {step.label}
                  </span>
                  {!step.done && step.pct > 0 && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{ background: 'rgba(245,132,31,0.1)', color: '#F5841F' }}>
                      {step.pct}%
                    </span>
                  )}
                  {!step.done && step.pct === 0 && (
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--t-text-muted)' }} />
                  )}
                  <span className="text-[9px] font-medium" style={{ color: 'var(--t-text-muted)' }}>{step.weight}%</span>
                </button>
              ))}
            </div>
          </motion.div>
        );
      })()}

      {/* Career Intelligence Hero Card */}
      <motion.div {...fadeIn(0.08)} className="nx-card p-6 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1B3461 0%, #253F78 100%)', border: 'none' }}>
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <Brain className="w-5 h-5" style={{ color: '#0D9488' }} />
            <span className="text-[10px] font-bold tracking-[0.15em] uppercase" style={{ color: '#0D9488' }}>
              Career Intelligence Engine
            </span>
          </div>
          {!hasCI ? (
            <>
              <h2 className="text-lg font-extrabold mb-1" style={{ color: '#F7FAFC' }}>
                Start Your Career Discovery
              </h2>
              <p className="text-sm mb-4 max-w-lg" style={{ color: '#9DB5DC' }}>
                Complete the 16-question discovery assessment to unlock personalized career insights, grade your skills across 12 dimensions, and access powerful career tools.
              </p>
              <Button onClick={() => navigate('/career-assessment')} data-testid="start-discovery-cta"
                className="h-10 px-5 text-sm font-bold rounded-lg"
                style={{ background: '#0D9488', color: '#fff' }}>
                Begin Discovery <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </>
          ) : (
            <>
              <h2 className="text-lg font-extrabold mb-2" style={{ color: '#F7FAFC' }}>
                {discoveryComplete ? 'Discovery Complete' : 'Discovery In Progress'}
              </h2>
              <div className="flex items-center gap-4 mb-3">
                <div className="flex-1">
                  <Progress value={completionPct} className="h-2" />
                </div>
                <span className="text-sm font-bold" style={{ color: '#0D9488' }}>{completionPct}%</span>
              </div>
              <div className="flex items-center gap-3 text-sm" style={{ color: '#9DB5DC' }}>
                <span>{ciData?.answered_count || 0}/{ciData?.total_questions || 16} questions</span>
                <span className="opacity-30">|</span>
                <span>{gradedCount} dimensions graded</span>
              </div>
              <div className="flex gap-3 mt-4">
                <Button onClick={() => navigate(discoveryComplete ? '/career-batteries' : '/career-assessment')}
                  data-testid="continue-ci-cta"
                  className="h-10 px-5 text-sm font-bold rounded-lg"
                  style={{ background: '#0D9488', color: '#fff' }}>
                  {discoveryComplete ? 'Assessment Batteries' : 'Continue Discovery'}
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button onClick={() => navigate('/career-intel')} data-testid="view-intel-cta"
                  className="h-10 px-5 text-sm font-medium rounded-lg"
                  style={{ background: 'rgba(255,255,255,0.08)', color: '#9DB5DC', border: '1px solid rgba(255,255,255,0.1)' }}>
                  View Dashboard
                </Button>
              </div>
            </>
          )}
        </div>
      </motion.div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: 'CI Progress', value: `${completionPct}%`,
            icon: Brain, accent: '#0D9488', desc: hasCI ? 'Discovery progress' : 'Not started',
          },
          {
            label: 'Dimensions', value: gradedCount,
            icon: Target, accent: '#3B82F6', desc: `of 12 graded`,
          },
          {
            label: 'Active Goals', value: sessions.length,
            icon: Sparkles, accent: '#1B3461', desc: sessions.length > 0 ? sessions[0]?.pathway_label || 'In progress' : 'None yet',
          },
          {
            label: 'Level', value: gamification?.level || 1,
            icon: Trophy, accent: '#F5841F', desc: `${gamification?.xp || 0} XP`,
          },
        ].map((s, i) => (
          <motion.div key={s.label} {...fadeIn(0.12 + i * 0.04)} className="nx-card p-4"
            data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: `${s.accent}12` }}>
                <s.icon className="w-4 h-4" style={{ color: s.accent }} />
              </div>
            </div>
            <p className="text-xl font-extrabold" style={{ color: 'var(--t-text-heading)' }}>{s.value}</p>
            <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--t-text-muted)' }}>{s.label}</p>
            <p className="text-[10px] mt-0.5" style={{ color: 'var(--t-text-muted)' }}>{s.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[
          {
            icon: BarChart3, label: 'Intelligence Dashboard', desc: 'View your career intelligence cards and grades',
            path: '/career-intel', accent: '#0D9488', available: true, pro: false,
          },
          {
            icon: ClipboardList, label: 'Reports', desc: 'Grade Report Card, Persona, Career Analysis',
            path: '/career-report', accent: '#1B3461', available: discoveryComplete, pro: true, gate: 'career_report',
          },
          {
            icon: Calculator, label: 'Career Tools', desc: 'Decision Calculator, Salary Simulator',
            path: '/career-tools', accent: '#3B82F6', available: discoveryComplete, pro: true, gate: 'career_calculator',
          },
          {
            icon: FileText, label: 'Resume Studio', desc: 'Build and manage your professional resume',
            path: '/resume', accent: '#16A34A', available: true, pro: false,
          },
          {
            icon: BookOpen, label: 'Community', desc: 'Connect with other career professionals',
            path: '/community', accent: '#805AD5', available: true, pro: false,
          },
          {
            icon: Shield, label: 'Settings', desc: 'Security, privacy, and account settings',
            path: '/settings', accent: '#718096', available: true, pro: false,
          },
        ].map((action, i) => {
          const Icon = action.icon;
          const locked = action.pro && !canAccess(action.gate);
          return (
            <motion.div key={action.label} {...fadeIn(0.2 + i * 0.04)}
              onClick={() => action.available ? navigate(action.path) : toast.info('Complete your Career Intelligence discovery first')}
              className="nx-card p-5 cursor-pointer nx-card-interactive"
              style={{ opacity: action.available ? 1 : 0.55 }}
              data-testid={`action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${action.accent}10` }}>
                  <Icon className="w-6 h-6" style={{ color: action.accent }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-lg font-bold truncate" style={{ color: 'var(--t-text-heading)' }}>{action.label}</p>
                    {locked && (
                      <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[9px] font-bold flex-shrink-0"
                        style={{ background: 'rgba(13,148,136,0.1)', color: '#0D9488' }}
                        data-testid={`pro-badge-${action.gate}`}>
                        <Award className="w-3 h-3" /> PRO
                      </span>
                    )}
                  </div>
                  <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>{action.desc}</p>
                </div>
                <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--t-text-muted)' }} />
              </div>
              {!action.available && (
                <div className="mt-3 text-xs font-semibold flex items-center gap-1.5" style={{ color: '#F5841F' }}>
                  <AlertCircle className="w-3.5 h-3.5" /> Complete CI discovery to unlock
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Active Goals */}
      {sessions.length > 0 && (
        <motion.div {...fadeIn(0.35)} className="nx-card p-5" data-testid="active-goals-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4" style={{ color: '#0D9488' }} />
              <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Active Career Goals</h3>
            </div>
            <button onClick={() => navigate('/career-intel')}
              className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1"
              style={{ color: '#0D9488' }} data-testid="view-all-goals-btn">
              View All <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2">
            {sessions.slice(0, 3).map((s, i) => (
              <div key={s.session_id} className="flex items-center gap-3 p-3 rounded-lg transition-all cursor-pointer"
                style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border-subtle)' }}
                onClick={() => navigate('/career-intel')}
                data-testid={`goal-${i}`}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: s.discovery_complete ? 'rgba(22,163,74,0.1)' : 'rgba(13,148,136,0.1)' }}>
                  {s.discovery_complete ? <CheckCircle className="w-4 h-4" style={{ color: '#16A34A' }} />
                    : <Brain className="w-4 h-4" style={{ color: '#0D9488' }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate" style={{ color: 'var(--t-text-heading)' }}>
                    {s.pathway_label || 'New Session'}
                  </p>
                  <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>
                    {s.answered_count}/{s.total_questions} questions | {s.completion_pct}% complete
                  </p>
                </div>
                <div className="w-16">
                  <Progress value={s.completion_pct} className="h-1.5" />
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Career Progress Timeline */}
      {timeline && timeline.milestones?.length > 0 && (
        <motion.div {...fadeIn(0.4)} className="nx-card p-5" data-testid="career-timeline-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(13,148,136,0.1)' }}>
                <Target className="w-4 h-4" style={{ color: '#0D9488' }} />
              </div>
              <div>
                <h3 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>Career Progress</h3>
                <p className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>
                  {timeline.completed} of {timeline.total} milestones reached
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-20 rounded-full overflow-hidden" style={{ background: 'var(--t-bg-section)' }}>
                <div className="h-full rounded-full transition-all" style={{
                  width: `${timeline.total > 0 ? (timeline.completed / timeline.total) * 100 : 0}%`,
                  background: 'linear-gradient(90deg, #0D9488, #14B8A6)',
                }} />
              </div>
              <span className="text-[10px] font-bold" style={{ color: '#0D9488' }}>
                {timeline.total > 0 ? Math.round((timeline.completed / timeline.total) * 100) : 0}%
              </span>
            </div>
          </div>

          <div className="relative" data-testid="timeline-track">
            {/* Vertical line */}
            <div className="absolute left-[15px] top-2 bottom-2 w-[2px]" style={{ background: 'var(--t-border)' }}>
              <div className="w-full rounded-full transition-all" style={{
                height: `${timeline.total > 0 ? (timeline.completed / timeline.total) * 100 : 0}%`,
                background: 'linear-gradient(180deg, #0D9488, #14B8A6)',
              }} />
            </div>

            {/* Milestones */}
            <div className="space-y-0">
              {timeline.milestones.map((m, i) => {
                const Icon = ICON_MAP[m.icon] || Circle;
                const dateStr = m.date ? new Date(m.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                return (
                  <motion.div
                    key={m.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i, duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    className="flex items-start gap-3 py-2.5 relative"
                    data-testid={`timeline-milestone-${m.id}`}
                  >
                    {/* Node */}
                    <div className="relative z-10 flex-shrink-0">
                      <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center transition-all"
                        style={m.complete ? {
                          background: '#0D9488',
                          boxShadow: '0 0 0 3px rgba(13,148,136,0.15)',
                        } : {
                          background: 'var(--t-bg-card-solid)',
                          border: '2px dashed var(--t-border)',
                        }}>
                        <Icon className="w-3.5 h-3.5" style={{ color: m.complete ? '#fff' : 'var(--t-text-muted)' }} />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-center gap-2">
                        <p className="text-[13px] font-semibold truncate" style={{
                          color: m.complete ? 'var(--t-text-heading)' : 'var(--t-text-muted)',
                        }}>
                          {m.label}
                        </p>
                        {m.complete && (
                          <CheckCircle className="w-3 h-3 flex-shrink-0" style={{ color: '#16A34A' }} />
                        )}
                      </div>
                      <p className="text-[11px] mt-0.5" style={{ color: 'var(--t-text-muted)' }}>
                        {m.description}
                      </p>
                    </div>

                    {/* Date */}
                    {dateStr && (
                      <span className="text-[10px] font-medium flex-shrink-0 pt-1" style={{ color: 'var(--t-text-muted)' }}>
                        {dateStr}
                      </span>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
